import { IWistiaOEmbed } from './types';

const WISTIA_OEMBED_URL = 'https://fast.wistia.com/oembed';

/** Share links (`https://<account>.wistia.com/s/<shareId>`) carry a share id, not a media id. */
const WISTIA_SHARE_URL = /^https?:\/\/[^/]*wistia\.(?:com|net)\/s\/[^/?#]+/i;

const EMBED_MEDIA_ID = /embed\/iframe\/([a-z0-9]+)/i;

export function isWistiaShareUrl(url: string): boolean {
  return WISTIA_SHARE_URL.test(url);
}

/**
 * The embed player (react-player) only plays Wistia `/medias/` and `/embed/` urls, and a
 * share link's id is not the media id. Wistia's oEmbed endpoint maps one to the other.
 *
 * Plain fetch, not getApi(): this is a third-party endpoint, and getApi() would send our
 * backend's base url and auth headers to it.
 */
export async function resolveWistiaShareUrl(url: string): Promise<string> {
  const response = await fetch(`${WISTIA_OEMBED_URL}?url=${encodeURIComponent(url)}`);
  if (!response.ok) {
    throw new Error(`Wistia oEmbed failed with ${response.status}`);
  }

  const { html } = (await response.json()) as IWistiaOEmbed;
  const mediaId = html?.match(EMBED_MEDIA_ID)?.[1];
  if (!mediaId) {
    throw new Error('Wistia oEmbed returned no media id');
  }

  return `https://fast.wistia.net/embed/iframe/${mediaId}`;
}

/** Returns a url the video players can load; anything that is not a Wistia share link is returned as is. */
export async function resolvePlayableVideoUrl(url: string): Promise<string> {
  if (!isWistiaShareUrl(url)) {
    return url;
  }

  try {
    return await resolveWistiaShareUrl(url);
  } catch (_e) {
    // Keep the original: the player's own error handling then reports and shows the failure.
    return url;
  }
}
